/**
 * Block demographics (GET /v1/tenant/block-demographics) and per-district
 * aggregation for the stats panel's Demographics view.
 *
 * Universes: total population (pop), voting-age population (vap, 18+), and
 * citizen voting-age population (cvap). Groups are the redistricting
 * breakdown: Hispanic or Latino (any race), then non-Hispanic race groups.
 * Groups are mutually exclusive and sum to each universe's total.
 *
 * Sums run client-side over the painting store's assignments, so the numbers
 * update live while painting — guest drafts included.
 */
import { create } from 'zustand';
import { apiFetch } from './api';

export type Universe = 'pop' | 'vap' | 'cvap';

export const UNIVERSE_LABELS: Record<Universe, { short: string; long: string }> = {
  pop: { short: 'Total pop.', long: 'Total population' },
  vap: { short: 'Voting age', long: 'Voting-age population (18+)' },
  cvap: { short: 'CVAP', long: 'Citizen voting-age population' },
};

export interface DemographicGroup {
  key: string;
  label: string;
}

export interface BlockDemographics {
  available: boolean;
  source: string | null;
  synthetic: boolean;
  groups: DemographicGroup[];
  /** GEOID -> row index into every column. */
  index: Map<string, number>;
  /** Field name -> per-block values. */
  columns: Map<string, Float64Array>;
  /** False when CVAP was not loaded (all zeros). */
  hasCvap: boolean;
}

export interface UniverseBreakdown {
  total: number;
  groups: Record<string, number>;
}

export type DistrictDemographics = Record<Universe, UniverseBreakdown>;

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export function parseBlockDemographics(payload: unknown): BlockDemographics {
  const record = isRecord(payload) ? payload : {};
  const fields = Array.isArray(record.fields) ? record.fields.filter((f): f is string => typeof f === 'string') : [];
  const geoids = Array.isArray(record.geoids) ? record.geoids.filter((g): g is string => typeof g === 'string') : [];
  const rawColumns: unknown[] = Array.isArray(record.columns) ? (record.columns as unknown[]) : [];
  const groups = Array.isArray(record.groups)
    ? record.groups
        .filter(isRecord)
        .map((g) => {
          const key = typeof g.key === 'string' ? g.key : '';
          return { key, label: typeof g.label === 'string' ? g.label : key };
        })
        .filter((g) => g.key)
    : [];
  const columns = new Map<string, Float64Array>();
  fields.forEach((name, i) => {
    const col: unknown = rawColumns[i];
    const arr = new Float64Array(geoids.length);
    if (Array.isArray(col)) {
      const values = col as unknown[];
      for (let j = 0; j < geoids.length; j += 1) {
        const v = values[j];
        arr[j] = typeof v === 'number' && Number.isFinite(v) ? v : 0;
      }
    }
    columns.set(name, arr);
  });
  const index = new Map<string, number>();
  geoids.forEach((g, i) => index.set(g, i));
  const cvapTotal = columns.get('cvap_total');
  let hasCvap = false;
  if (cvapTotal) {
    for (let i = 0; i < cvapTotal.length; i += 1) {
      if ((cvapTotal[i] ?? 0) > 0) {
        hasCvap = true;
        break;
      }
    }
  }
  return {
    available: record.available === true && geoids.length > 0,
    source: typeof record.source === 'string' ? record.source : null,
    synthetic: record.synthetic === true,
    groups,
    index,
    columns,
    hasCvap,
  };
}

export async function fetchBlockDemographics(): Promise<BlockDemographics> {
  return parseBlockDemographics(await apiFetch<unknown>('/v1/tenant/block-demographics'));
}

function emptyBreakdown(groups: DemographicGroup[]): UniverseBreakdown {
  const g: Record<string, number> = {};
  for (const { key } of groups) g[key] = 0;
  return { total: 0, groups: g };
}

function emptyDistrict(groups: DemographicGroup[]): DistrictDemographics {
  return { pop: emptyBreakdown(groups), vap: emptyBreakdown(groups), cvap: emptyBreakdown(groups) };
}

const UNIVERSES: Universe[] = ['pop', 'vap', 'cvap'];

function addRow(target: DistrictDemographics, demo: BlockDemographics, row: number): void {
  for (const u of UNIVERSES) {
    const bucket = target[u];
    bucket.total += demo.columns.get(`${u}_total`)?.[row] ?? 0;
    for (const { key } of demo.groups) {
      bucket.groups[key] = (bucket.groups[key] ?? 0) + (demo.columns.get(`${u}_${key}`)?.[row] ?? 0);
    }
  }
}

/** Per-district sums (0-based district index) plus the whole jurisdiction. */
export function computeDistrictDemographics(
  assignments: Record<string, number>,
  demo: BlockDemographics,
  districtCount: number,
): { districts: DistrictDemographics[]; jurisdiction: DistrictDemographics } {
  const districts = Array.from({ length: districtCount }, () => emptyDistrict(demo.groups));
  const jurisdiction = emptyDistrict(demo.groups);
  for (const [geoid, row] of demo.index) {
    addRow(jurisdiction, demo, row);
    const d = assignments[geoid];
    if (d !== undefined && d >= 0 && d < districtCount) addRow(districts[d] as DistrictDemographics, demo, row);
  }
  return { districts, jurisdiction };
}

export function formatShare(part: number, whole: number): string {
  if (whole <= 0) return '—';
  const pct = (part / whole) * 100;
  return `${pct >= 10 || pct === 0 ? pct.toFixed(0) : pct.toFixed(1)}%`;
}

/* ── Lazy, once-per-tenant cache ──────────────────────────────────── */

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

interface DemographicsState {
  tenantSlug: string | null;
  state: LoadState;
  data: BlockDemographics | null;
  error: string | null;
  ensure: (tenantSlug: string) => Promise<void>;
}

export const useDemographicsStore = create<DemographicsState>()((set, get) => ({
  tenantSlug: null,
  state: 'idle',
  data: null,
  error: null,
  ensure: async (tenantSlug) => {
    const s = get();
    if (s.tenantSlug === tenantSlug && (s.state === 'ready' || s.state === 'loading')) return;
    set({ tenantSlug, state: 'loading', error: null, data: null });
    try {
      const data = await fetchBlockDemographics();
      if (get().tenantSlug === tenantSlug) set({ state: 'ready', data });
    } catch (error) {
      if (get().tenantSlug === tenantSlug) {
        set({
          state: 'error',
          error: error instanceof Error ? error.message : 'Could not load demographics.',
        });
      }
    }
  },
}));
